import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { AbEntitySchema } from "#shared/schemas/abEnrich.ts";
import {
  MbBatchSizeSchema,
  MbCascadeDumpSchema,
  MbCoverageSchema,
  MbEntitySchema,
  MbLinkSchema,
  MbStatusSchema,
  MbWipeResultSchema,
  type MbBatchSize,
  type MbLinkEntityType,
} from "#shared/schemas/mbEnrich.ts";
import { notifyError } from "@web/features/diagnostics/notifyError.ts";
import { api } from "@web/lib/api.ts";
import { coyLog } from "@web/lib/coyLog.ts";
import { toast } from "sonner";

function failToast(err: unknown, fallback: string): void {
  notifyError(err, { fallbackCode: "SAVE_FAILED", actionLabel: fallback });
}

function invalidateMb(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["mb-status"] });
  void qc.invalidateQueries({ queryKey: ["mb-link"] });
  void qc.invalidateQueries({ queryKey: ["mb-coverage"] });
  void qc.invalidateQueries({ queryKey: ["mb-cascade"] });
  void qc.invalidateQueries({ queryKey: ["entity"] });
}

export function useMbStatus(
  scope:
    | {
        playlistId?: string | null;
        localPlaylistId?: string | null;
      }
    | null = null,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((query: {
          state: { data: z.infer<typeof MbStatusSchema> | undefined };
        }) => number | false);
  },
) {
  return useQuery({
    queryKey: ["mb-status", scope?.playlistId ?? null, scope?.localPlaylistId ?? null],
    enabled: options?.enabled,
    queryFn: async () => {
      const parts: string[] = [];
      if (scope?.playlistId) parts.push(`playlist_id=${encodeURIComponent(scope.playlistId)}`);
      if (scope?.localPlaylistId) {
        parts.push(`local_playlist_id=${encodeURIComponent(scope.localPlaylistId)}`);
      }
      const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
      return MbStatusSchema.parse(await api(`/api/mb/status${qs}`));
    },
    refetchInterval: options?.refetchInterval,
  });
}

export function useMbLink(entityType: string | null, entityId: string | null) {
  return useQuery({
    queryKey: ["mb-link", entityType, entityId],
    enabled: Boolean(entityType && entityId),
    queryFn: async () =>
      z
        .object({
          link: MbLinkSchema.nullable(),
          entity: MbEntitySchema.nullable(),
          acousticbrainz: AbEntitySchema.nullable().optional(),
        })
        .parse(
          await api(
            `/api/mb/links/${encodeURIComponent(entityType!)}/${encodeURIComponent(entityId!)}`,
          ),
        ),
    refetchInterval: (q) => {
      void q;
      return 3_000;
    },
  });
}

export function useMbCascadeDump(trackId: string | null, enabled = true) {
  return useQuery({
    queryKey: ["mb-cascade", trackId],
    enabled: Boolean(trackId) && enabled,
    queryFn: async () =>
      MbCascadeDumpSchema.parse(
        await api(`/api/mb/cascade/${encodeURIComponent(trackId!)}`),
      ),
    refetchInterval: 3_000,
  });
}

export function useMbEntity(mbid: string | null) {
  return useQuery({
    queryKey: ["mb-entity", mbid],
    enabled: Boolean(mbid),
    queryFn: async () =>
      z
        .object({ entity: MbEntitySchema })
        .parse(await api(`/api/mb/entities/${encodeURIComponent(mbid!)}`)),
  });
}

export function useMbCoverage(playlistId: string | null) {
  return useQuery({
    queryKey: ["mb-coverage", playlistId],
    queryFn: async () => {
      const qs = playlistId
        ? `?playlist_id=${encodeURIComponent(playlistId)}`
        : "";
      return MbCoverageSchema.parse(await api(`/api/mb/coverage${qs}`));
    },
  });
}

export function useSaveMbContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contact: string) =>
      MbStatusSchema.parse(await api("/api/mb/settings", { method: "PATCH", json: { contact } })),
    onSuccess: () => {
      toast.success("MusicBrainz contact saved");
      void qc.invalidateQueries({ queryKey: ["mb-status"] });
    },
    onError: (err) => failToast(err, "Could not save contact"),
  });
}

export function useStartMbBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      size: MbBatchSize;
      playlist_id?: string | null;
      local_playlist_id?: string | null;
      force?: boolean;
      /** Sheet sync icon — no success toast. */
      quiet?: boolean;
    }) => {
      MbBatchSizeSchema.parse(input.size);
      return z
        .object({ run: z.unknown() })
        .parse(
          await api("/api/mb/runs", {
            method: "POST",
            json: {
              size: input.size,
              ...(input.playlist_id ? { playlist_id: input.playlist_id } : {}),
              ...(input.local_playlist_id ? { local_playlist_id: input.local_playlist_id } : {}),
              ...(input.force ? { force: true } : {}),
            },
          }),
        );
    },
    onSuccess: (_b, input) => {
      coyLog.info(
        `MB enrich batch started · size=${input.size}` +
          (input.playlist_id ? ` · playlist=${input.playlist_id}` : "") +
          (input.local_playlist_id ? ` · local=${input.local_playlist_id}` : "") +
          (input.force ? " · force" : ""),
      );
      if (!input.quiet) {
        toast.success(
          input.local_playlist_id
            ? `Enrich started · local playlist · next ${input.size}${input.force ? " (force)" : ""}`
            : input.playlist_id
              ? `Enrich started · playlist · next ${input.size}${input.force ? " (force)" : ""}`
            : `Enrich started · next ${input.size}${input.force ? " (force)" : ""}`,
        );
      }
      invalidateMb(qc);
    },
    onError: (err) => failToast(err, "Could not start enrich"),
  });
}

export function useStartMbEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      entity_type: MbLinkEntityType;
      entity_id: string;
      force?: boolean;
      allow_name_search?: boolean;
      /** Sheet sync icon — no success toast. */
      quiet?: boolean;
    }) => {
      const { quiet: _quiet, ...body } = input;
      return z
        .object({ run: z.unknown() })
        .parse(await api("/api/mb/runs/entity", { method: "POST", json: body }));
    },
    onSuccess: (_b, vars) => {
      coyLog.info(
        `MB per-entity enrich started · ${vars.entity_type}` +
          (vars.allow_name_search ? " · name search" : ""),
      );
      if (!vars.quiet) {
        toast.success(
          vars.allow_name_search ? "Name search started" : "Enrich started for this entity",
        );
      }
      invalidateMb(qc);
    },
    onError: (err) => failToast(err, "Could not start enrich"),
  });
}

export function useWipeFetchedMb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      MbWipeResultSchema.parse(
        await api("/api/mb/wipe-fetched", { method: "POST", json: { confirm: true } }),
      ),
    onSuccess: (result) => {
      coyLog.info(
        `MB wipe · entities=${result.entities_deleted} links=${result.links_deleted} ab=${result.ab_deleted} manual_kept=${result.manual_kept}`,
      );
      toast.success(
        `Wiped ${result.entities_deleted.toLocaleString()} entities · kept ${result.manual_kept} manual MBID${result.manual_kept === 1 ? "" : "s"}`,
      );
      invalidateMb(qc);
    },
    onError: (err) => failToast(err, "Could not wipe enrichment"),
  });
}

export function usePauseMbPass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/api/mb/runs/pause", { method: "POST" }),
    onSuccess: () => {
      toast.message("Enrich paused");
      void qc.invalidateQueries({ queryKey: ["mb-status"] });
    },
    onError: (err) => failToast(err, "Could not pause"),
  });
}

export function useResumeMbPass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/api/mb/runs/resume", { method: "POST" }),
    onSuccess: () => {
      toast.success("Enrich resumed");
      void qc.invalidateQueries({ queryKey: ["mb-status"] });
    },
    onError: (err) => failToast(err, "Could not resume"),
  });
}

export function useStopMbPass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/api/mb/runs/stop", { method: "POST" }),
    onSuccess: () => {
      toast.message("Enrich stopped — stored data kept");
      invalidateMb(qc);
    },
    onError: (err) => failToast(err, "Could not stop"),
  });
}

export function useManualMbLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      entityType: MbLinkEntityType;
      entityId: string;
      mbid: string;
    }) =>
      z
        .object({ link: MbLinkSchema.nullable() })
        .parse(
          await api(
            `/api/mb/links/${encodeURIComponent(input.entityType)}/${encodeURIComponent(input.entityId)}/manual`,
            { method: "POST", json: { mbid: input.mbid } },
          ),
        ),
    onSuccess: () => {
      toast.success("MBID locked");
      invalidateMb(qc);
    },
    onError: (err) => failToast(err, "Could not lock MBID"),
  });
}
